import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  WavelightWorkflowState,
  WorkflowStartResponse,
  WorkflowVideoUploadResponse,
} from './types';
import {
  Gender,
  RacialDenomination,
  WavelightMode,
  WorkflowMeasurements,
  WorkflowPerson,
  WorkflowStudy,
} from '../../types';
import {
  confirmVideoUpload,
  finishWorkflow,
  getPreSignedURL,
  startWorkflow,
  testSpeedUpload,
  updateMeasurements,
  updateMetaData,
  updatePersonData,
  updateStudyData,
  uploadFileOn,
  uploadHashInVideo,
} from './userAPI';
import storage from '../../utils/storage';
import { notifyErrorToBugsnag } from '../../utils';

// Update Geo Location for Meta Data
export const setMetaData = createAsyncThunk(
  'wavelight/gelo-location',
  async (_, { rejectWithValue }) => {
    try {
      const position: any = await new Promise((resolve, reject) => {
        if ('geolocation' in navigator) {
          navigator.geolocation.getCurrentPosition(resolve, reject);
        } else {
          reject('Geolocation is not supported by this browser.');
        }
      });

      // Após obter sucesso na localização, retorna os dados necessários.
      return {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };
    } catch (error) {
      // Caso haja algum erro, rejeita a Promise com o valor do erro.
      return rejectWithValue(error);
    }
  },
);

const TEST_FILE_SIZE = 1024 * 1024; // 1MB

// Função para criar um arquivo de teste
const createTestFile = () => {
  const buffer = new ArrayBuffer(TEST_FILE_SIZE);
  const view = new Uint8Array(buffer);
  for (let i = 0; i < view.length; i++) {
    view[i] = 0;
  }
  return new File([buffer], 'test-upload-file', {
    type: 'application/octet-stream',
  });
};

export const uploadVideo = createAsyncThunk(
  'wavelight/workflow/upload-video',
  async (_, thunkAPI) => {
    const currentState: any = thunkAPI.getState();
    const workflow: WavelightWorkflowState = currentState.workflow;

    // Verifica se há um vídeo a ser carregado
    if (!workflow?.data?.video) return { wavelightId: '' };

    try {
      // 1. Inicia o workflow criando um ticket
      const ticket: WorkflowStartResponse = await startWorkflow(
        WavelightMode.ANALYSIS,
      );
      const wavelightId = ticket.id;

      // 1. Cria um arquivo de teste e mede a velocidade de upload
      const testFile = createTestFile();
      const startTime = performance.now();

      await testSpeedUpload(wavelightId, testFile);

      const endTime = performance.now();
      const duration = (endTime - startTime) / 1000; // Duração em segundos
      const uploadSpeedMbps = (TEST_FILE_SIZE * 8) / (duration * 1024 * 1024); // Velocidade de upload em Mbps

      // Estima o tempo necessário para o upload do vídeo real
      const videoSizeMB = workflow.data.video.size / (1024 * 1024); // Tamanho do vídeo em MB
      const estimatedUploadTimeSeconds = (videoSizeMB * 8) / uploadSpeedMbps; // Tempo estimado em segundos

      console.log(
        `Tempo estimado para o upload: ${estimatedUploadTimeSeconds.toFixed(2)} segundos`,
      );

      thunkAPI.dispatch({
        type: 'workflow/setEstimatedUploadTime',
        payload: estimatedUploadTimeSeconds,
      });

      // 4. Lida com velocidade de upload lenta
      if (uploadSpeedMbps < 3.5) {
        // Despacha uma ação ou define um estado para controlar a exibição de um alerta de upload lento
        thunkAPI.dispatch({
          type: 'workflow/setSlowUploadAlert',
          payload: true,
        });
      }

      // 2. Obtém a URL pré-assinada para upload do vídeo
      const extension = `.${workflow?.data?.video.name.split('.').pop()}`;
      const fileUploadRequest: WorkflowVideoUploadResponse =
        await getPreSignedURL(wavelightId, { extension });

      // 5. Realiza o upload do arquivo real
      await uploadFileOn(
        fileUploadRequest,
        workflow?.data?.video,
        estimatedUploadTimeSeconds,
      );

      // 6. Mando o hash do arquivo
      await uploadHashInVideo(wavelightId, workflow?.data?.video);

      // 7. confirma o envio
      await confirmVideoUpload(wavelightId);

      return { wavelightId };
    } catch (error) {
      console.error('Erro durante o upload do vídeo:', error);
      throw error;
    }
  },
);

// Start and Finish the entire Workflow process...
export const uploadData = createAsyncThunk(
  'wavelight/workflow/upload-data',
  async (_, thunkAPI) => {
    // Acesso ao estado atual
    const currentState: any = thunkAPI.getState();
    const workflow: WavelightWorkflowState = currentState.workflow;
    const wavelightId = workflow?.wavelightId;

    if (!wavelightId) return false;

    // 5. Send Person Data
    await updatePersonData(wavelightId, workflow?.data?.person);
    // 6. Send Study Data
    await updateStudyData(wavelightId, workflow?.data?.study);
    // 7. Send Measurements
    await updateMeasurements(wavelightId, workflow?.data?.measurements);
    // 8. Send Metadatas
    await updateMetaData(wavelightId, workflow?.data?.metadata);
    // 9. Close this Workflow
    const res = await finishWorkflow(wavelightId);

    return !!res;
  },
);

export const OXIMETER_TAGS = '@wavelight_OXIMETER_TAGS';
export const PRESSURE_GAUGE = '@wavelight_PRESSURE_GAUGE';
export const CELLPHONE = '@wavelight_CELLPHONE';
// export const GLUCOSE_EXTRA_INFO = '@wavelight/GLUCOSE_EXTRA_INFO';
export const TAGS = '@wavelight/TAGS';
export const HEIGHT = '@wavelight/HEIGHT';
export const WEIGHT = '@wavelight/WEIGHT';
export const AGE = '@wavelight/AGE';
export const GENDER = '@wavelight/GENDER';
export const RACIAL_DENOMINATION = '@wavelight/RACIAL_DENOMINATION';

const getInitialState = (): WavelightWorkflowState => ({
  estimatedUploadTime: 0,
  slowUploadAlert: false,
  currentStepIndex: 0,
  requestId: null,
  // Current Wavelight ID
  wavelightId: null,
  // Loading
  isLoading: false,
  isUploading: false,
  // Upload Completed
  videoUploadCompleted: false,
  dataUploadCompleted: false,
  // Errors
  dataUploadError: false,
  videoUploadError: false,
  videoUploadErrorMessage: undefined,
  // Data
  data: {
    video: null,
    study: {
      tags: storage.get(TAGS) || [],
      phone: storage.get(CELLPHONE) || [],
      oximeterModel: storage.get(OXIMETER_TAGS) || [],
      pressureGaugeModel: storage.get(PRESSURE_GAUGE) || [],
      extraDescription: '',
    },
    person: {
      height: storage.get(HEIGHT) || 0,
      weight: storage.get(WEIGHT) || 0,
      age: storage.get(AGE) || 0,
      gender: storage.get(GENDER) || Gender.UNDEFINED,
      racialDenomination:
        storage.get(RACIAL_DENOMINATION) || RacialDenomination.UNDEFINED,
    },
    measurements: {
      bpm: 0,
      bloodPreassure: {
        systolic: 0,
        diastolic: 0,
      },
      bloodO2Saturation: 0,
      respiratoryRate: 0,
      glucose: 0,
    },
    metadata: {
      browserInfo: {
        userAgent: navigator.userAgent,
        language: navigator.language,
      },
      screenInfo: {
        width: window.screen.width,
        height: window.screen.height,
      },
      geoLocalization: null,
    },
  },
});
const initialState: WavelightWorkflowState = getInitialState();

export const workflowSlice = createSlice({
  name: 'workflow',
  initialState,
  reducers: {
    // Controll
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    nextStep: (state) => {
      state.currentStepIndex = state.currentStepIndex + 1;
    },
    previousStep: (state) => {
      state.currentStepIndex = state.currentStepIndex - 1;
    },
    resetState: () => getInitialState(),
    setRequestId: (state, action: PayloadAction<string>) => {
      state.requestId = action.payload;
    },
    // Video
    setVideo: (state, action: PayloadAction<File>) => {
      state.data.video = action.payload;
    },
    setSlowUploadAlert: (state, action: PayloadAction<boolean>) => {
      state.slowUploadAlert = action.payload;
    },
    setEstimatedUploadTime: (state, action: PayloadAction<number>) => {
      state.estimatedUploadTime = action.payload;
    },
    // Study
    setStudyData: (state, action: PayloadAction<WorkflowStudy>) => {
      state.data.study = action.payload;
    },
    resetStudyData: (state) => {
      state.data.study = {
        extraDescription: '',
        oximeterModel: [],
        phone: [],
        pressureGaugeModel: [],
        tags: [],
      };
    },
    loadStudyData: (state) => {
      state.data.study = {
        tags: storage.get(TAGS) || [],
        phone: storage.get(CELLPHONE) || [],
        oximeterModel: storage.get(OXIMETER_TAGS) || [],
        pressureGaugeModel: storage.get(PRESSURE_GAUGE) || [],
      };
    },
    // Personal data
    setPersonData: (state, action: PayloadAction<WorkflowPerson>) => {
      state.data.person = action.payload;
    },
    resetPersonData: (state) => {
      state.data.person = {
        age: 0,
        gender: Gender.UNDEFINED,
        height: 0,
        racialDenomination: RacialDenomination.UNDEFINED,
        weight: 0,
      };
    },
    loadPersonData: (state) => {
      state.data.person = {
        height: storage.get(HEIGHT) || 0,
        weight: storage.get(WEIGHT) || 0,
        age: storage.get(AGE) || 0,
        gender: storage.get(GENDER) || Gender.UNDEFINED,
        racialDenomination:
          storage.get(RACIAL_DENOMINATION) || RacialDenomination.UNDEFINED,
      };
    },
    // Measurements
    setMeasurementsData: (
      state,
      action: PayloadAction<WorkflowMeasurements>,
    ) => {
      state.data.measurements = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Upload Video
    builder.addCase(uploadVideo.fulfilled, (state, { payload }) => {
      state.isUploading = false;
      state.videoUploadCompleted = true;
      state.wavelightId = payload.wavelightId;
    });
    builder.addCase(uploadVideo.pending, (state) => {
      state.isUploading = true;
      state.videoUploadError = false;
      state.slowUploadAlert = false;
    });
    builder.addCase(uploadVideo.rejected, (state, action) => {
      state.isUploading = false;
      state.videoUploadCompleted = false;
      state.videoUploadError = true;
      state.slowUploadAlert = false;
      state.videoUploadErrorMessage = action.error?.message;

      notifyErrorToBugsnag(state, action);
    });
    // Upload Data
    builder.addCase(uploadData.fulfilled, (state, { payload }) => {
      state.isLoading = false;
      state.dataUploadCompleted = payload;

      storage.set(TAGS, state.data.study.tags);
      storage.set(CELLPHONE, state.data.study.phone);
      storage.set(OXIMETER_TAGS, state.data.study.oximeterModel);
      storage.set(PRESSURE_GAUGE, state.data.study.pressureGaugeModel);
      storage.set(AGE, state.data.person.age);
      storage.set(GENDER, state.data.person.gender);
      storage.set(HEIGHT, state.data.person.height);
      storage.set(RACIAL_DENOMINATION, state.data.person.racialDenomination);
      storage.set(WEIGHT, state.data.person.weight);
    });
    builder.addCase(uploadData.pending, (state) => {
      state.isLoading = true;
      state.dataUploadError = false;
    });
    builder.addCase(uploadData.rejected, (state, action) => {
      state.isLoading = false;
      state.dataUploadCompleted = false;
      state.dataUploadError = true;
      notifyErrorToBugsnag(state, action);
    });
    // Meta Data - Geo Localization
    builder.addCase(setMetaData.fulfilled, (state, { payload }) => {
      state.data.metadata.geoLocalization = {
        latitude: payload.latitude,
        longitude: payload.longitude,
      };
    });
    builder.addCase(setMetaData.rejected, (state, action) => {
      state.data.metadata.geoLocalization = null;
      notifyErrorToBugsnag(state, action);
    });
  },
});

export const {
  // Controll
  setLoading,
  resetState,
  nextStep,
  previousStep,
  setRequestId,
  // Video
  setSlowUploadAlert,
  setVideo,
  setEstimatedUploadTime,
  // Study
  setStudyData,
  resetStudyData,
  loadStudyData,
  // Personal data
  setPersonData,
  resetPersonData,
  loadPersonData,
  // Measurements
  setMeasurementsData,
} = workflowSlice.actions;

export default workflowSlice.reducer;
